import React from "react"
import TransitionGroup from "react-transition-group/CSSTransitionGroup"
import classNames from "classnames"
import qs from "query-string"
import { connect } from "react-redux"
import styled, { css } from "styled-components"
import { responsive } from "buoy-theme"
import ChatItem from "./ChatItem"
import Completion from "../../completion/"
import EmptyCompletion from "../../completion/components/EmptyCompletion"
import SymptomBubble from "../../search/components/SymptomBubbleContainer"
import { SVG_URL } from "../../common/constants"
import Disclaimer from "./Disclaimer"
import { scroll, inIframe } from "../../common/common"
import DxocCloseMessage from "./DxocCloseMessage"
import smoothscroll from "smoothscroll-polyfill"
smoothscroll.polyfill()

const ChatWindowWrapper = styled.div`
  width: 100%;
  max-width: 560px;
  display: flex;
  justify-content: center;
  ${props =>
    props.isProfile
      ? css`
          ${responsive("padding", "0 32", "0 32px", "0 32px", "0 32px", "0")};
        `
      : css`
          ${responsive(
            "padding",
            "0 32px",
            "0 32px",
            "0 32px",
            "0 32px",
            "0 16px"
          )};
        `};
`

const ChatWindow = styled.div`
  transition: border 1s ease;
  max-width: 600px;
  width: 100%;
  margin-bottom: ${props => (props.symptomInput ? `400px` : 0)};
  transition: margin-top 0.8s ease;

  ${props =>
    props.isProfile || props.iFrame
      ? css`
          margin-top: 0;
          padding-bottom: 0;
        `
      : css`
          padding-bottom: 40px;
          ${responsive("margin-top", "67px", "67px", "60px", "60px", "60px")};
        `};
`

const BubbleLoading = styled.div`
  margin-top: ${props => (!props.isProfile ? "1rem" : "")};
  width: 100%;
  display: flex;
  justify-content: flex-begin;
`

// Filters for all items before or at the current index and the image for the
// active question
const chatFilter = (item, activeIndex) => {
  return !item.filter && item.index <= activeIndex && item.type !== "clarifier"
}

const chatItemSelect = (
  item,
  index,
  list,
  activeIndex,
  completed,
  brandConfigs,
  isProfile
) => {
  // for determining opacity:
  let lastResponse = list
    .filter(
      i => i.type === "response" || (i.type === "symptomBubble" && i.text)
    )
    .slice(-1)[0]
  switch (item.type) {
    case "completion":
      return (
        <Completion key={`completionBubble ${index}`} isProfile={isProfile} />
      )
    case "symptomBubble":
      return <SymptomBubble key={`symptomBubble ${index}`} item={item} />
    case "emptyCompletion":
      return (
        <EmptyCompletion
          item={item}
          key={`emptyCompletion ${index}`}
          brandConfigs={brandConfigs}
        />
      )
    case "dxocCloseMessage":
      return (
        <DxocCloseMessage
          key={`dxocCloseMessage ${index}`}
          item={item}
          brandConfigs={brandConfigs}
          isLast={lastResponse ? item.index > lastResponse.index : true}
        />
      )

    default:
      return (
        <ChatItem
          item={item}
          prev={index > 0 ? list[index - 1] : null}
          key={`${item.text} ${index}`}
          active={item.index === activeIndex}
          completed={completed}
          brandConfigs={brandConfigs}
          isProfile={isProfile}
          isLastResponse={lastResponse && lastResponse.index === item.index}
          isLast={lastResponse ? item.index > lastResponse.index : true}
        />
      )
  }
}

const ChatList = ({
  chatItems,
  activeIndex,
  loading,
  completed,
  symptomInput,
  brandConfigs,
  hasResponse,
  isProfile = false,
}) => {
  const symptomTest = qs.parse(window.location.search).symptom_test
  let iFrame = inIframe()
  return (
    <div id="chatlist" className={classNames("chatlist-container")}>
      <ChatWindowWrapper isProfile={isProfile}>
        <ChatWindow
          id="chatWindow"
          first={symptomTest ? chatItems.length <= 2 : !hasResponse}
          symptomInput={symptomInput}
          isProfile={isProfile}
          iFrame={iFrame}
        >
          <Disclaimer />
          <TransitionGroup
            transitionName="chat-item"
            transitionEnterTimeout={500}
            transitionLeaveTimeout={500}
            transitionAppear={true}
            transitionAppearTimeout={500}
          >
            {chatItems
              .filter(item => chatFilter(item, activeIndex))
              .map((item, index, list) =>
                chatItemSelect(
                  item,
                  index,
                  list,
                  activeIndex,
                  completed,
                  brandConfigs,
                  isProfile
                )
              )}
          </TransitionGroup>
          {loading && (
            <BubbleLoading isProfile={isProfile}>
              <img
                src={
                  brandConfigs && brandConfigs.name
                    ? `${SVG_URL}loading-wheel-gray.gif`
                    : `${SVG_URL}loading-wheel.gif`
                }
                style={{ height: 36 }}
                alt="Loading"
              />
            </BubbleLoading>
          )}
        </ChatWindow>
      </ChatWindowWrapper>
    </div>
  )
}

const scrollCompletionContainer = () => {
  let completionContainer = document.getElementById("completionContainer")
  let chatWindow = document.getElementById("chatWindow")

  if (chatWindow && completionContainer) {
    let paddingBottom = parseInt(
      window.getComputedStyle(chatWindow).getPropertyValue("padding-bottom")
    )
    chatWindow = parseInt(chatWindow.scrollHeight)
    completionContainer = parseInt(completionContainer.scrollHeight)

    setTimeout(() => {
      const val = chatWindow - completionContainer - paddingBottom
      window.scrollTo({ top: val, behavior: "smooth" })
    }, 100)
  }
}

class ChatListContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = { active: -1 }
  }

  componentDidUpdate(prevProps) {
    const { chatItems } = this.props
    const recentItem = chatItems.slice(-1)[0]

    if (
      chatItems.length > prevProps.chatItems.length &&
      recentItem &&
      recentItem.index !== 0
    ) {
      if (recentItem.type === "completion") {
        scrollCompletionContainer()
      } else {
        scroll()
      }
    }
  }

  render() {
    return <ChatList {...this.props} />
  }
}

const mapStateToProps = (state, ownProps) => ({
  chatItems: ownProps.chatItems || state.chatList.items,
  activeIndex:
    ownProps.activeIndex !== undefined
      ? ownProps.activeIndex
      : state.chatList.active,
  loading: state.loading,
  adjusting: state.identifiers.adjusting,
  completed: ownProps.completed || state.completion.completed,
  currentVisit: state.account.currentVisit,
  symptomInput:
    state.loading &&
    state.chatList.items[state.chatList.active] &&
    (state.chatList.items[state.chatList.active].type === "symptomBubble" ||
      state.chatList.items[state.chatList.active].type === "clarifier" ||
      state.chatList.items[state.chatList.active].filter),
  hasResponse: state.chatList.items.findIndex(i => i.type === "response") > -1,
})

ChatListContainer = connect(mapStateToProps)(ChatListContainer)

export { ChatListContainer as default, ChatItem, ChatList }



// WEBPACK FOOTER //
// ./assets/interview/components/ChatList.js